/** @format */

import { Subject, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import type {
  LoyaltyBalance,
  PersonalInformation,
  ICartItem,
  CartActionProduct,
  IHomrAlertMessage,
  AvailabilityByLocationStore,
  StoreByLocationInformation,
} from '@shared/data-access/interfaces';

@Injectable({
  providedIn: 'root',
})
export class EventBusService {
  private readonly subject$ = new Subject<EmitEvent<Events>>();
  private subjectSubscriptionMap: { [key: string]: Subscription } = {};
  private history: Partial<Record<Events, EmitEvent<Events>[]>> = {};

  /**
   * @description
   * Event listener for provided Events enum value. Executes the callback provided
   * when the Event is emitted
   */
  public on<K extends Events>(event: K, action: (event: EventPayloadMap[K]) => void): Subscription {
    this.subjectSubscriptionMap[event] = this.subject$
      .pipe(
        filter((e): e is EmitEvent<K> => e.name === event),
        map((e) => {
          return e.value;
        }),
      )
      .subscribe(action);

    return this.subjectSubscriptionMap[event];
  }

  /**
   * @description
   * Emits the provided Event
   */
  public emit<K extends Events>(event: EmitEvent<K>) {
    const eventHistory = this.history[event.name];
    if (eventHistory === undefined) {
      this.history[event.name] = [event];
    } else {
      eventHistory.push(event);
    }

    this.subject$.next(event);
  }

  /**
   * @description
   * Unsubscribe from provided Events enum value
   */
  public unsubscribe(event: Events) {
    // Ensure there is something to unsubscribe from
    if (this.subjectSubscriptionMap[event] !== undefined) {
      this.subjectSubscriptionMap[event].unsubscribe();
    }
  }

  public checkHistoryForEvent<K extends Events>(event: K) {
    return this.history[event];
  }
}

/**
 * @description
 * EmitEvent
 */
export class EmitEvent<K extends Events> {
  public readonly value: EventPayloadMap[K];

  // Allow the value argument to be omitted if the event does not require a payload
  constructor(
    public readonly name: K,
    ...args: undefined extends EventPayloadMap[K]
      ? [value?: EventPayloadMap[K]]
      : [value: EventPayloadMap[K]]
  ) {
    // This `as` is required because TypeScript isn't smart enough to realize that args[0] can only
    // be undefined if EventPayloadMap[K] is undefined
    this.value = args[0] as EventPayloadMap[K];
  }
}

/**
 * @description
 * Events to be emitted and listened for
 */
export enum Events {
  ChangeStore,
  NoCart,
  SkuStoreSelected,
  CartUpdateEvent,
  PostATCEvent,
  PaypalClick,
  PaypalError,
  TimerEnded,
  LoyaltyBalanceFetched,
  LoyaltyProfileFetched,
  ValidateForms,
  AddressChanged,
  PCSPointsUpdated,
  IndyPayMainScriptLoaded,
  AccjsMainScriptLoaded,
  PaymentButtonsScriptLoaded,
  PaymentButtonsCheckoutScriptLoaded,
  PaymentButtonsErrored,
  CartRemoveProduct,
  EmptyFavorites,
  EnableFooter,
  RemoveOOSProduct,
  SaveForLaterV2Success,
  SaveForLaterV2DeleteSuccess,
  ValidateSavedAddressForm,
  ScorecardPlusTermsExpired,
  ExpressCheckoutConfirmationClosed,
  AccjsMainScriptLoadFailed,
}

export interface EventPayloadMap {
  [Events.ChangeStore]: undefined;
  [Events.NoCart]: undefined;
  [Events.SkuStoreSelected]: {
    sku: string;
    store: StoreByLocationInformation | AvailabilityByLocationStore;
  };
  [Events.CartUpdateEvent]: { failure?: boolean; complete?: boolean } | undefined;
  [Events.PostATCEvent]: { sku: string } | undefined;
  [Events.PaypalClick]: undefined;
  [Events.PaypalError]: undefined;
  [Events.TimerEnded]: undefined;
  [Events.LoyaltyBalanceFetched]: LoyaltyBalance;
  [Events.LoyaltyProfileFetched]: PersonalInformation;
  [Events.ValidateForms]: undefined;
  [Events.AddressChanged]: undefined;
  [Events.PCSPointsUpdated]: undefined;
  [Events.IndyPayMainScriptLoaded]: undefined;
  [Events.AccjsMainScriptLoaded]: undefined;
  [Events.PaymentButtonsScriptLoaded]: undefined;
  [Events.PaymentButtonsCheckoutScriptLoaded]: undefined;
  [Events.PaymentButtonsErrored]: undefined;
  [Events.CartRemoveProduct]: ICartItem;
  [Events.EmptyFavorites]: undefined;
  [Events.EnableFooter]: undefined;
  [Events.RemoveOOSProduct]: { product: CartActionProduct };
  [Events.SaveForLaterV2Success]: undefined;
  [Events.SaveForLaterV2DeleteSuccess]: undefined;
  [Events.ValidateSavedAddressForm]: boolean;
  [Events.ScorecardPlusTermsExpired]: IHomrAlertMessage;
  [Events.ExpressCheckoutConfirmationClosed]: undefined;
  [Events.AccjsMainScriptLoadFailed]: undefined;
}
